import * as THREE from 'three';
import { glowTex } from './ground';

/* ============================================================
   接地影 —— 讓量體「站在地上」而不是「貼在地圖上」
   ============================================================

   使用者 2026-08-04 在質感這一輪選的方案：柔化真陰影 ＋ **全場接地影**。

   ## 為什麼真陰影補不了這件事

   `render/scene.ts` 的陰影相機是**綁在玩家身上的 30×22 小框**（1024–2048²
   要看得清機體，就不可能同時罩住 480 m 見方的場地）。所以離玩家十幾公尺
   以外的建築、路障、車隊全部沒有影子 —— 它們在畫面上是「浮在地面貼圖上的
   立體圖案」，而這正是使用者說的「像貼在地圖上」。

   接地影不是陰影的替代品，它做的是**環境遮蔽**：物體與地面的接縫本來就
   照不到天光，那一圈暗是「這兩個東西是接觸的」的唯一視覺證據。
   真陰影講太陽在哪，接地影講東西站在哪 —— 兩者不衝突，疊起來才對。

   ## 三個實作決定

   1. **一顆 `InstancedMesh`，一個 draw call。** 場上會有數百圈，
      每圈一顆 mesh 的話 draw call 直接翻三倍。
   2. **格點去重。** 資料來源是 `ruin-kit` 的 `put()`（每一個零件都會呼叫），
      一棟樓有二十幾個貼地零件 —— 逐件放圈會疊成一坨純黑。
      所以按 `CELL` 公尺的格點收斂，每格只留最大的那一個。
   3. **NormalBlending 不用 MultiplyBlending。** 乘算的重疊會相乘（兩圈疊起來
      是 0.5×0.5 ＝ 0.25，讀成一個黑洞）；一般混合的重疊有上限，
      而接地影的工作是「有一圈暗」不是「這裡有多暗」。 */

/** 格點邊長（公尺）。比多數零件寬、比一棟樓窄 —— 一棟樓收斂成幾圈，
    一台車收斂成一圈。 */
var CELL = 4;
/** 單圈的半徑上限。沒有上限的話一面 40 m 的牆會生出一個 40 m 的黑餅。 */
var R_MAX = 7;
/** 貼地判準：零件底面低於這個高度才算「站在地上」。 */
var GROUND_Y = 1.3;

var _foot = new Map<number, { x: number, z: number, r: number }>();
var _mesh: THREE.InstancedMesh | null = null;
var _mat: THREE.MeshBasicMaterial | null = null;
var _obj = new THREE.Object3D();

/** 柔化的接地影材質。敵人那一層（`render/enemy-field.ts`）也用這一支 ——
    兩邊的濃度與衰減曲線必須一致，否則同一張畫面上會有兩種影子。 */
export function softShadowMat(opacity: number) {
  return new THREE.MeshBasicMaterial({
    map: glowTex() as any, color: 0x000000,
    transparent: true, opacity: opacity, depthWrite: false, fog: true,
  });
}

function mat() {
  if (_mat) return _mat;
  /* 黑色 × 柔光圓的 alpha ＝ 中心濃、邊緣散的一圈暗。
     `glowTex()` 本來就是給發光體用的白色徑向漸層，這裡只是把顏色乘成黑 ——
     多開一張貼圖沒有意義，而且共用可以少一次 GPU 上傳。 */
  _mat = new THREE.MeshBasicMaterial({
    map: glowTex() as any, color: 0x000000,
    transparent: true, opacity: 0.52, depthWrite: false,
    /* `fog: true` 是必要的：接地影在 100 m 外要跟著霧淡掉，
       否則遠處會出現一片濃度不變的黑點，讀成畫面壞掉。 */
    fog: true,
  });
  return _mat;
}

/* ---------- 靜態（建築、雜物、地標）---------- */

/** `ruin-kit` 的 `put()` 每放一個零件就呼叫一次。**只收貼地的**。
    ⚠️ 這裡不可以做任何配置或三角函數 —— 一張圖會呼叫它三千多次。 */
export function noteFootprint(y: number, w: number, h: number, d: number, x: number, z: number) {
  if (y - h * 0.5 > GROUND_Y) return;                 // 不是貼地的零件
  var r = Math.min(Math.max(w, d) * 0.62, R_MAX);
  if (r < 0.6) return;                                // 太小的（輪子、窗框）不值一個 draw
  var key = (Math.round(x / CELL) + 2048) * 4096 + Math.round(z / CELL) + 2048;
  var e = _foot.get(key);
  if (!e) _foot.set(key, { x: x, z: z, r: r });
  else if (r > e.r) { e.x = x; e.z = z; e.r = r; }    // 每格只留最大的那一個
}

export function resetFootprints() { _foot.clear(); }

/** 把收集到的足跡蓋成一顆 `InstancedMesh` 掛進 `group`。
    在 `flushBuckets()` 之後呼叫（那時候零件都已經 `put` 過了）。 */
export function buildContactShadows(group: THREE.Group) {
  var n = _foot.size;
  if (!n) return 0;
  var geo = new THREE.PlaneGeometry(1, 1);
  geo.rotateX(-Math.PI / 2);
  var m = new THREE.InstancedMesh(geo, mat(), n);
  var i = 0;
  _foot.forEach(function (f) {
    /* y = 0.05：要在地面貼花（0.02）與雲影（0.04）**之上**，
       否則接地影會被那兩層的乘算吃掉一半濃度。 */
    _obj.position.set(f.x, 0.05, f.z);
    _obj.scale.set(f.r * 3.1, 1, f.r * 3.1);   // 柔光圓的可見半徑約貼圖的 1/3
    _obj.updateMatrix();
    m.setMatrixAt(i++, _obj.matrix);
  });
  m.instanceMatrix.needsUpdate = true;
  m.frustumCulled = false;
  m.renderOrder = 3;                            // 貼花 1、雲影 2、接地影 3
  group.add(m);
  _mesh = m;
  resetFootprints();
  return n;
}

export function contactShadowMesh() { return _mesh; }
